/**
 * Inscricoes: cada plano de cada cliente, num lugar so (Vitor, 15/09: Inscricoes e Matriculas "devem ser a mesma coisa").
 * As ativas se dividem em "Em dia" (nenhuma pendencia) e "Com pendencia" (teste vencido, mensalidade vencida ou sem valor,
 * conta nao declarada): quem decide o que e pendencia e o backend, pelos modulos (ADR-001). As outras situacoes
 * (aguardando o inicio, suspensas, encerradas, todas) vem da lista de matriculas, com busca no servidor. Clicar abre a
 * matricula, com a trilha e as acoes.
 *
 * De 20 em 20 (spec 058, Fase 2): as abas por situacao pedem a pagina ao servidor, com o total do filtro. "Em dia" e
 * "Com pendencia" paginam na tela: a lista de ativas ja chega inteira, e a pendencia e calculada pelos modulos (nao e
 * coluna do banco), entao o filtro dela nao desce pro servidor sem guardar a pendencia em algum lugar.
 */
import { Abas, Aviso, CabecalhoDePagina, Campo, CampoSelecao, Carregando, LinkInterno, Motivo, Pilha, POR_PAGINA, Selo, Tabela, Vazio, formatarData, formatarDia, type Coluna } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { STATUS_DA_MATRICULA, erroDe, useApi, type MatriculaPainel, type ModuloCorretora, type ModuloEtapas, type ModuloMensalidade, type Pessoinha, type StatusMatricula } from "../api";
import { agruparPorCpf, paginaDosGrupos } from "../agrupar";
import { usePagina } from "../pagina";
import { usePlanos } from "./Matriculas";
import { TOM_DA_MENSALIDADE } from "./Mensalidades";
import { SubcontaDoPlano } from "../subconta";

type Pendencia = { codigo: string; rotulo: string; detalhe: string | null };
type Inscricao = MatriculaPainel & { pessoa: Pessoinha; email: string | null; modulos: { etapas?: ModuloEtapas; corretora?: ModuloCorretora; mensalidade?: ModuloMensalidade }; pendencias: Pendencia[] };
type Resposta = { contagem: { em_dia: number; com_pendencia: number; traders: number }; itens: Inscricao[] };
type Aba = "em-dia" | "pendencia" | "aguardando" | "suspensas" | "encerradas" | "todas";

const ABAS: Aba[] = ["em-dia", "pendencia", "aguardando", "suspensas", "encerradas", "todas"];
const STATUS_DA_ABA: Record<"aguardando" | "suspensas" | "encerradas" | "todas", StatusMatricula | null> = { aguardando: "AGUARDANDO_INICIO", suspensas: "SUSPENSA", encerradas: "ENCERRADA", todas: null };
const ORIGEM: Record<string, string> = { COMPRA: "Compra", CORTESIA: "Cortesia" };
const soDigitos = (s: string) => s.replace(/\D/g, "");
/** A subconta (Prop, nelogica-v1) ou a conta na corretora (Global, nelogica-v2, spec 043): cada modulo da o nome dele. */
const numeroDaConta = (i: Inscricao) => i.modulos.corretora?.subconta ?? (i.modulos.corretora as { conta?: string } | undefined)?.conta ?? null;

export function PaginaInscricoes() {
  const { ler } = useApi();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ["inscricoes"], queryFn: () => ler<Resposta>("/v1/admin/inscricoes") });
  const aba = (ABAS.includes(params.get("aba") as Aba) ? params.get("aba") : "em-dia") as Aba;
  // Trocar de aba volta pra pagina 1 na mesma navegacao (spec 058).
  const mudarAba = (v: Aba) => { const p = new URLSearchParams(params); if (v === "em-dia") p.delete("aba"); else p.set("aba", v); p.delete("pagina"); setParams(p, { replace: true }); };
  const c = q.data?.contagem;
  const ativas = c ? c.em_dia + c.com_pendencia : 0;
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Inscrições"
        descricao={c ? `Cada plano de cada cliente. Com plano ativo agora: ${c.traders} cliente${c.traders === 1 ? "" : "s"} em ${ativas} inscriç${ativas === 1 ? "ão" : "ões"}. Clique numa linha pra abrir a trilha e agir.` : "Cada plano de cada cliente. Clique numa linha pra abrir a trilha e agir."}
      />
      <Abas
        rotulo="Situação"
        valor={aba}
        aoMudar={mudarAba}
        abas={[
          { valor: "em-dia", rotulo: "Em dia", contagem: c?.em_dia },
          { valor: "pendencia", rotulo: "Com pendência", contagem: c?.com_pendencia },
          { valor: "aguardando", rotulo: "Aguardando início" },
          { valor: "suspensas", rotulo: "Suspensas" },
          { valor: "encerradas", rotulo: "Encerradas" },
          { valor: "todas", rotulo: "Todas" },
        ]}
      />
      {aba === "em-dia" || aba === "pendencia" ? (
        q.isLoading ? <Carregando compacto texto="Buscando as inscrições" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : <Ativas itens={q.data?.itens ?? []} comPendencia={aba === "pendencia"} />
      ) : (
        <PorSituacao key={aba} status={STATUS_DA_ABA[aba]} />
      )}
    </Pilha>
  );
}

/** As ativas: em dia ou com pendencia, com etapa, conta e mensalidade (o que cada modulo sabe). Filtro na propria tela. */
function Ativas({ itens, comPendencia }: { itens: Inscricao[]; comPendencia: boolean }) {
  const navegar = useNavigate();
  const [busca, setBusca] = useState("");
  const [plano, setPlano] = useState("");
  const [etapa, setEtapa] = useState("");
  const pag = usePagina("pagina", [busca, plano, etapa]);
  const planos = [...new Map(itens.map((i) => [i.plano.slug, i.plano.nome])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const etapas = [...new Map(itens.filter((i) => i.modulos.etapas).map((i) => [i.modulos.etapas!.etapa, i.modulos.etapas!.etapa_rotulo])).entries()];
  const t = busca.trim().toLowerCase();
  const d = soDigitos(t);
  const linhas = itens.filter((i) =>
    (comPendencia ? i.pendencias.length > 0 : i.pendencias.length === 0) &&
    (!plano || i.plano.slug === plano) &&
    (!etapa || i.modulos.etapas?.etapa === etapa) &&
    (!t || i.pessoa.nome.toLowerCase().includes(t) || (i.email ?? "").includes(t) || (d.length >= 3 && (soDigitos(i.pessoa.cpf).includes(d) || (numeroDaConta(i) ?? "").includes(d)))),
  );
  // Spec 030: planos do mesmo CPF ficam juntos, na posicao do mais recente deles. A ordem entre pessoas nao muda.
  // Spec 058: a pagina sai depois do agrupamento, pra um cliente nao mudar de lugar conforme a pagina.
  const agrupadas = agruparPorCpf(linhas);
  const daPagina = paginaDosGrupos(agrupadas, pag.pagina);
  const grupos = new Map(daPagina.map((a) => [a.linha.id, a.grupo]));
  const temEtapas = itens.some((i) => i.modulos.etapas);
  const temConta = itens.some((i) => i.modulos.corretora);
  const temMensalidade = itens.some((i) => i.modulos.mensalidade);
  const colunas: Coluna<Inscricao>[] = [
    {
      chave: "trader",
      rotulo: "Cliente",
      // Spec 030: o nome leva pra ficha da PESSOA; o plano continua acessivel pela linha e pela coluna Plano.
      celula: (i) => {
        const g = grupos.get(i.id);
        if (g && !g.primeira) {
          return (
            <>
              <span className="mtc-linha" style={{ gap: "var(--e-2)", paddingLeft: "var(--e-4)" }}>
                <span className="mtc-texto-2" aria-hidden="true">&#8627;</span>
                <Selo tom="info">{g.posicao}&#186; plano</Selo>
                <span className="mtc-texto-2">mesmo cliente</span>
              </span>
              <span className="mtc-tabela__apoio mtc-mono" style={{ paddingLeft: "var(--e-4)" }}>{i.pessoa.cpf}</span>
            </>
          );
        }
        return (
          <>
            <LinkInterno href={`/traders/${i.pessoa.id}`}>{i.pessoa.nome}</LinkInterno>
            <span className="mtc-tabela__apoio mtc-mono">{i.pessoa.cpf}{g && g.total > 1 ? ` · ${g.total} planos` : ""}</span>
          </>
        );
      },
    },
    // Spec 030, decisao 3: o nome leva pra PESSOA, entao o plano precisa do proprio link, visivel.
    { chave: "plano", rotulo: "Plano", celula: (i) => <><LinkInterno href={`/matriculas/${i.id}`}>{i.plano.nome}</LinkInterno>{i.modulos.corretora?.plataforma_rotulo && <span className="mtc-tabela__apoio">{i.modulos.corretora.plataforma_rotulo}</span>}</> },
    ...(temEtapas ? [{ chave: "etapa", rotulo: "Etapa", celula: (i: Inscricao) => { const e = i.modulos.etapas; if (!e) return <span className="mtc-texto-2">sem etapa</span>; return <>{e.etapa_rotulo}<span className="mtc-tabela__apoio">{e.etapa === "TESTE" ? (e.teste_ate ? `teste até ${formatarDia(e.teste_ate)}` : "teste sem prazo") : (e.inicio_da_etapa ? `desde ${formatarDia(e.inicio_da_etapa)}` : "início não informado")}</span></>; } }] : []),
    ...(temConta ? [{ chave: "conta", rotulo: "Conta", celula: (i: Inscricao) => (numeroDaConta(i) ? <><span className="mtc-mono">{numeroDaConta(i)}</span>{(i.modulos.corretora?.mesa_nome ?? i.modulos.corretora?.mesa) && <span className="mtc-tabela__apoio">{i.modulos.corretora?.mesa_nome ?? i.modulos.corretora?.mesa}</span>}</> : <span className="mtc-texto-2">sem conta</span>) }] : []),
    // O nome da cobranca vem do backend (a Global chama de "Taxa de manutencao", spec 064); sem ele, "Mensalidade".
    ...(temMensalidade ? [{ chave: "mensalidade", rotulo: itens.find((i) => i.modulos.mensalidade?.rotulo)?.modulos.mensalidade?.rotulo ?? "Mensalidade", celula: (i: Inscricao) => { const m = i.modulos.mensalidade; if (!m) return null; return m.situacao === "SEM_COBRANCA" ? <span className="mtc-texto-2">{m.sem_cobranca_motivo ?? "Sem cobrança agora"}</span> : <><Selo tom={TOM_DA_MENSALIDADE[m.situacao]}>{m.situacao_rotulo}</Selo>{m.proxima && <span className="mtc-tabela__apoio">próxima em {m.proxima.vencimento_formatado}</span>}</>; } }] : []),
    comPendencia
      ? { chave: "pendencia", rotulo: "Pendência", celula: (i) => <span className="mtc-pilha" style={{ gap: "var(--e-2)" }}>{i.pendencias.map((p) => <Motivo key={p.codigo} titulo={p.rotulo} detalhe={p.detalhe ?? undefined} />)}</span> }
      : { chave: "desde", rotulo: "Ativa desde", celula: (i) => <span className="mtc-mono">{i.ativada_em ? formatarData(i.ativada_em) : ""}</span> },
  ];
  const filtrou = Boolean(t || plano || etapa);
  return (
    <>
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
        <Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Nome, CPF, e-mail ou subconta" />
        <CampoSelecao rotulo="Plano" valor={plano} aoMudar={setPlano} vazio="Todos os planos" opcoes={planos.map(([valor, rotulo]) => ({ valor, rotulo }))} />
        {temEtapas && <CampoSelecao rotulo="Etapa" valor={etapa} aoMudar={setEtapa} vazio="Todas as etapas" opcoes={etapas.map(([valor, rotulo]) => ({ valor, rotulo }))} />}
      </div>
      <Tabela
        linhas={daPagina.map((a) => a.linha)}
        paginacao={{ ...pag, total: agrupadas.length }}
        chave={(i) => i.id}
        aoClicar={(i) => navegar(`/matriculas/${i.id}`)}
        vazio={<Vazio titulo={filtrou ? "Nada com esse filtro" : comPendencia ? "Nenhuma pendência" : "Ninguém aqui ainda"}>{filtrou ? "Tente outro nome, CPF, plano ou etapa." : comPendencia ? "Todas as inscrições ativas estão em dia." : "As matrículas ativas aparecem aqui assim que são liberadas."}</Vazio>}
        colunas={colunas}
      />
    </>
  );
}

/** Aguardando o inicio, suspensas, encerradas ou todas: a lista de matriculas de 20 em 20, com busca e total no servidor. */
function PorSituacao({ status }: { status: StatusMatricula | null }) {
  const { ler } = useApi();
  const navegar = useNavigate();
  const planos = usePlanos();
  const [plano, setPlano] = useState("");
  const [texto, setTexto] = useState("");
  const [busca, setBusca] = useState("");
  useEffect(() => { const t = setTimeout(() => setBusca(texto.trim()), 300); return () => clearTimeout(t); }, [texto]);
  const buscaValida = busca.length >= 3 ? busca : "";
  const pag = usePagina("pagina", [plano, buscaValida]);
  const qs = new URLSearchParams({ pagina: String(pag.pagina), por_pagina: String(POR_PAGINA), ...(status ? { status } : {}), ...(plano ? { plano } : {}), ...(buscaValida ? { busca: buscaValida } : {}) });
  const q = useQuery({ queryKey: ["matriculas", status, plano, buscaValida, pag.pagina], queryFn: () => ler<{ itens: MatriculaPainel[]; total: number; pagina: number }>(`/v1/admin/matriculas?${qs}`), placeholderData: (anterior) => anterior });
  const itens = q.data?.itens ?? [];
  // Spec 030: agrupa aqui tambem. A primeira versao so agrupava a lista de ativas, e o e2e pegou a aba Todas sem marca.
  const agrupadas = agruparPorCpf(itens.filter((m): m is MatriculaPainel & { pessoa: { id: string; nome: string; cpf: string } } => Boolean(m.pessoa)));
  const grupos = new Map(agrupadas.map((a) => [a.linha.id, a.grupo]));
  const semPessoa = itens.filter((m) => !m.pessoa);
  const linhas = [...agrupadas.map((a) => a.linha), ...semPessoa];
  return (
    <>
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
        <Campo rotulo="Buscar" valor={texto} aoMudar={setTexto} placeholder="Nome, CPF ou subconta" dica="A partir de 3 letras ou números." />
        <CampoSelecao rotulo="Plano" valor={plano} aoMudar={setPlano} vazio="Todos os planos" opcoes={(planos.data?.itens ?? []).map((p) => ({ valor: p.slug, rotulo: p.nome }))} />
      </div>
      {q.isError && <Aviso tipo="erro">{erroDe(q.error)}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando as inscrições" /> : (
        <>
          <Tabela
            linhas={linhas}
            paginacao={{ pagina: q.data?.pagina ?? pag.pagina, aoMudar: pag.aoMudar, total: q.data?.total ?? 0 }}
            chave={(m) => m.id}
            aoClicar={(m) => navegar(`/matriculas/${m.id}`)}
            vazio={<Vazio titulo="Nenhuma inscrição">{busca || plano ? "Nada com esse filtro." : "Nenhuma nesta situação."}</Vazio>}
            colunas={[
              {
                chave: "trader",
                rotulo: "Cliente",
                celula: (m) => {
                  const g = grupos.get(m.id);
                  if (g && !g.primeira) {
                    return (
                      <>
                        <span className="mtc-linha" style={{ gap: "var(--e-2)", paddingLeft: "var(--e-4)" }}>
                          <span className="mtc-texto-2" aria-hidden="true">&#8627;</span>
                          <Selo tom="info">{g.posicao}&#186; plano</Selo>
                          <span className="mtc-texto-2">mesmo cliente</span>
                        </span>
                        <span className="mtc-tabela__apoio mtc-mono" style={{ paddingLeft: "var(--e-4)" }}>{m.pessoa?.cpf}</span>
                      </>
                    );
                  }
                  return (
                    <>
                      {m.pessoa ? <LinkInterno href={`/traders/${m.pessoa.id}`}>{m.pessoa.nome}</LinkInterno> : <span>Sem nome</span>}
                      <span className="mtc-tabela__apoio mtc-mono">{m.pessoa?.cpf}{g && g.total > 1 ? ` \u00b7 ${g.total} planos` : ""}</span>
                    </>
                  );
                },
              },
              { chave: "plano", rotulo: "Plano", celula: (m) => <><LinkInterno href={`/matriculas/${m.id}`}>{m.plano.nome}</LinkInterno><SubcontaDoPlano subconta={m.subconta} /></> },
              { chave: "status", rotulo: "Situação", celula: (m) => <Selo tom={STATUS_DA_MATRICULA[m.status].tom}>{STATUS_DA_MATRICULA[m.status].rotulo}</Selo> },
              { chave: "origem", rotulo: "Origem", celula: (m) => ORIGEM[m.origem] ?? m.origem },
              { chave: "comprada", rotulo: "Comprada em", celula: (m) => <span className="mtc-mono">{m.comprada_em ? formatarData(m.comprada_em) : ""}</span> },
              { chave: "ativada", rotulo: "Ativada em", celula: (m) => <span className="mtc-mono">{m.ativada_em ? formatarData(m.ativada_em) : ""}</span> },
            ]}
          />
        </>
      )}
    </>
  );
}
